import React from 'react';

// Design-system button (ported from the handoff bundle's Button spec onto the
// app's Tailwind tokens, so it themes with light/dark automatically).
//
// Variants:
//   primary   — brand green fill (the one CTA per surface)
//   secondary — bordered, neutral; default for everything else
//   ghost     — borderless, low-emphasis
//   danger    — red outline; destructive actions (red is intentionally not
//               remapped in tailwind.config.js)
//   accent    — marker yellow; reserve for a single celebratory CTA
//
// States follow the spec: hover darkens/washes, active gives a subtle
// scale-down, disabled is 50% opacity, loading swaps in a spinner and
// disables. Focus ring comes from the global :focus-visible rule.

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
type Size = 'xs' | 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700',
  secondary: 'border border-slate-300 bg-card text-slate-700 hover:bg-slate-100',
  ghost: 'text-slate-700 hover:bg-slate-100',
  danger: 'border border-red-300 text-red-700 hover:bg-red-50',
  accent: 'bg-marker text-ink hover:bg-marker-deep',
};

// Sizes match the app's four established densities: xs = chip buttons,
// sm = compact side-panel CTAs, md = options/settings CTAs, lg = hero CTAs.
const SIZE: Record<Size, string> = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-3 py-1.5 text-sm',
  lg: 'px-4 py-2 text-sm',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Show a spinner and disable the button. */
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Stretch to the container width. */
  block?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  block = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={[
        'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded font-medium',
        'transition duration-150 ease-out active:scale-[0.985]',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        block ? 'w-full' : '',
        className ?? '',
      ].join(' ')}
      {...rest}
    >
      {loading ? <Spinner /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}

function Spinner() {
  return (
    <svg
      className="h-3.5 w-3.5 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}
